import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, BookOpen, Wand2, Users, Star, ArrowRight, Book } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { PageTransition, JellyButton } from "../components/common";
import SEOHead, { generateWebsiteSchema, generateOrganizationSchema, generateSoftwareAppSchema } from "../components/common/SEOHead";
import Footer from "../components/layout/Footer";

const LandingPage = () => {
  // Combined structured data for homepage
  const structuredData = [
    generateWebsiteSchema(),
    generateOrganizationSchema(),
    generateSoftwareAppSchema()
  ];
  const { user } = useAuth();
  const navigate = useNavigate();

  const features = [
    { icon: Wand2, title: "AI-Powered Stories", description: "Unique tales generated just for your child", color: "violet" },
    { icon: Users, title: "Custom Characters", description: "Upload photos to create personalized characters", color: "amber" },
    { icon: Book, title: "Digital Storybooks", description: "Full storybooks with cover and illustrated pages", color: "emerald" }
  ];

  return (
    <PageTransition>
      <SEOHead 
        title="StoryWeaver - Create Personalized AI Storybooks for Kids"
        description="Turn your child into the hero of their own illustrated adventure. AI generates both story and beautiful artwork. Create magical personalized storybooks in minutes."
        url="/"
        structuredData={structuredData}
      />
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="hero-gradient pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="flex-1 text-center lg:text-left">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                >
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-100 text-violet-700 text-sm font-medium mb-6">
                    <Sparkles className="w-4 h-4" />
                    AI-Illustrated Storybooks
                  </span>
                </motion.div>
                
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.3 }}
                  className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Create Magical<br />
                  <span className="text-violet-600">Illustrated Storybooks</span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                  className="text-lg text-slate-600 mb-8 max-w-xl mx-auto lg:mx-0"
                >
                  Turn your child into the hero of their own illustrated adventure. AI generates both story and beautiful artwork.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25, duration: 0.3 }}
                  className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
                >
                  <JellyButton
                    variant="primary"
                    onClick={() => navigate(user ? "/create" : "/login")}
                    data-testid="hero-start-btn"
                  >
                    <Wand2 className="w-5 h-5" />
                    Create Storybook
                  </JellyButton>
                  <JellyButton
                    variant="secondary"
                    onClick={() => navigate("/marketplace")}
                    data-testid="hero-explore-btn"
                  >
                    <BookOpen className="w-5 h-5" />
                    Explore Templates
                  </JellyButton>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1, duration: 0.4 }}
                className="flex-1 relative"
              >
                <div className="relative w-full max-w-lg mx-auto">
                  <div className="absolute inset-0 bg-gradient-to-r from-violet-400 to-amber-400 rounded-3xl blur-3xl opacity-30 animate-float" />
                  <picture>
                    <source
                      type="image/webp"
                      srcSet="/images/hero-mobile.webp 512w, /images/hero-tablet.webp 768w, /images/hero-desktop.webp 1024w"
                      sizes="(max-width: 640px) 512px, (max-width: 1024px) 768px, 512px"
                    />
                    <img
                      src="/images/hero-fallback.png"
                      alt="Joey's magical dragon adventure - AI-generated storybook illustration"
                      className="relative w-full rounded-3xl shadow-2xl"
                      width="512"
                      height="493"
                      fetchpriority="high"
                      decoding="async"
                    />
                  </picture>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Featured Storybook Section - Static */}
        <section className="py-16 px-4 bg-gradient-to-br from-amber-50 via-pink-50 to-violet-50">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 text-amber-700 text-sm font-medium mb-4">
                <Star className="w-4 h-4 fill-amber-500" />
                Featured Storybook
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                See What You Can Create
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="max-w-4xl mx-auto"
            >
              <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                <div className="flex flex-col lg:flex-row">
                  {/* Story Cover */}
                  <div className="lg:w-1/2 relative">
                    <div className="aspect-[4/3] lg:aspect-auto lg:h-full bg-gradient-to-br from-violet-100 to-amber-100">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_80668f19-affd-43cd-87d8-6c90bf3b332f/artifacts/h2k9h3we_Monosnap%20Shared%20Story%20_%20StoryWeaver%20-%20Google%20Chrom.png"
                        alt="The Magical Forest Adventure - A girl with her magic wand and puppy in an enchanted forest" 
                        className="w-full h-full object-cover"
                        width="600"
                        height="450"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-500 text-white text-xs font-bold shadow-lg">
                        <Star className="w-3 h-3 fill-white" /> Featured
                      </span>
                    </div>
                  </div>

                  {/* Story Details */}
                  <div className="lg:w-1/2 p-6 lg:p-8 flex flex-col justify-center">
                    <div className="mb-4">
                      <span className="inline-block px-3 py-1 rounded-full bg-violet-100 text-violet-700 text-xs font-medium capitalize mb-3">
                        Fantasy
                      </span>
                      <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                        The Magical Forest Adventure
                      </h3>
                      <p className="text-slate-600 text-sm mb-4">
                        12 illustrated pages • For ages 4-8
                      </p>
                    </div>

                    {/* Features Highlight */}
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center gap-3 text-sm text-slate-600">
                        <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                          <Users className="w-4 h-4 text-violet-600" />
                        </div>
                        <span>Custom character based on your child</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-slate-600">
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                          <Wand2 className="w-4 h-4 text-amber-600" />
                        </div>
                        <span>AI-generated unique story and illustrations</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-slate-600">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                          <BookOpen className="w-4 h-4 text-emerald-600" />
                        </div>
                        <span>Downloadable PDF to keep forever</span>
                      </div>
                    </div>

                    {/* First Page Preview */}
                    <div className="bg-slate-50 rounded-xl p-4 mb-6">
                      <p className="text-slate-600 text-sm italic line-clamp-3">
                        &ldquo;Deep in the heart of an enchanted forest, where fireflies danced among the colorful mushrooms and ancient trees whispered secrets, a curious girl with curly brown hair set off on a magical adventure with her loyal puppy companion...&rdquo;
                      </p>
                    </div>

                    <JellyButton
                      variant="primary"
                      onClick={() => window.open("https://storyweavestudio.com/shared/DQ45bYL-6O7ylvjDuof0rg", "_blank")}
                      className="w-full justify-center"
                      data-testid="featured-story-read-btn"
                    >
                      <BookOpen className="w-5 h-5" />
                      Read This Story
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </JellyButton>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Call to action */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center mt-8"
            >
              <p className="text-slate-600 mb-4">Every story is unique, personalized, and created in minutes!</p>
              <div className="flex justify-center">
                <JellyButton
                  variant="secondary"
                  onClick={() => navigate(user ? "/create" : "/register")}
                  className="bg-white"
                >
                  <Wand2 className="w-5 h-5" />
                  Create Your Own Story
                </JellyButton>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                Full Digital Storybooks<br />With AI Illustrations
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Each story comes with a cover and 10-25 beautifully illustrated pages
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {features.map((feature, i) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="card-feature text-center"
                >
                  <div className={`w-16 h-16 rounded-2xl bg-${feature.color}-100 flex items-center justify-center mx-auto mb-6`}>
                    <feature.icon className={`w-8 h-8 text-${feature.color}-600`} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                    {feature.title}
                  </h3>
                  <p className="text-slate-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-violet-600 to-violet-800">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Ready to Create Magic?
            </h2>
            <p className="text-violet-200 mb-8 text-lg">
              Start creating personalized storybooks that your children will love!
            </p>
            <div className="flex justify-center">
              <JellyButton
                variant="secondary"
                onClick={() => navigate(user ? "/create" : "/login")}
                className="bg-white text-violet-700 hover:bg-violet-50"
              >
                <Sparkles className="w-5 h-5" />
                Get Started Free
              </JellyButton>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </PageTransition>
  );
};

export default LandingPage;
